import { useState } from 'react';
import { api, handleApiError } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { userStatusLabels, userStatusColors, formatDate } from '../lib/labels';
import { PaginatedResponse } from '@ouagaliv/shared';

interface RowUser {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  vehicleType?: string;
  isAvailable?: boolean;
  rating?: number;
}

export default function Users() {
  const clients = useFetch<PaginatedResponse<RowUser>>('/admin/clients');
  const agents = useFetch<PaginatedResponse<RowUser>>('/admin/agents');
  const [actionError, setActionError] = useState('');
  const [actingId, setActingId] = useState('');

  const toggleStatus = async (id: string, current: string) => {
    const next = current === 'active' ? 'suspended' : 'active';
    setActingId(id);
    setActionError('');
    try {
      await api.patch(`/admin/agents/${id}/status`, { status: next });
      agents.refetch();
    } catch (err) {
      setActionError(handleApiError(err));
    } finally {
      setActingId('');
    }
  };

  return (
    <div className="page-transition">
      <div className="container pt-6">
        <div className="page-head">
          <div>
            <h1 className="page-title">Utilisateurs</h1>
            <p className="page-subtitle">Clients et agents de la plateforme</p>
          </div>
        </div>

        {actionError && (
          <div className="card" style={{ borderColor: 'var(--color-error)', marginBottom: 16 }}>
            <span style={{ color: 'var(--color-error)', fontSize: 14 }}>{actionError}</span>
          </div>
        )}

        <div className="card" style={{ marginBottom: 24 }}>
          <h2 className="text-lg font-semibold mb-4">Agents ({agents.data?.total ?? '…'})</h2>
          {agents.isLoading ? (
            <div className="state-box"><span className="loading-spinner" /></div>
          ) : agents.error ? (
            <div className="state-box">{agents.error}</div>
          ) : !agents.data?.items.length ? (
            <div className="state-box">Aucun agent pour le moment.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Nom</th><th>Contact</th><th>Véhicule</th><th>Note</th><th>Statut</th><th></th></tr>
                </thead>
                <tbody>
                  {agents.data.items.map(a => (
                    <tr key={a.id}>
                      <td><strong>{a.fullName}</strong></td>
                      <td>{a.email}<br /><span style={{ color: 'var(--color-text-secondary)' }}>{a.phone}</span></td>
                      <td>{a.vehicleType ?? '-'}</td>
                      <td>{a.rating ?? '-'}{a.isAvailable ? ' · en ligne' : ''}</td>
                      <td><span className={`badge ${userStatusColors[a.status] ?? 'badge-gray'}`}>{userStatusLabels[a.status] ?? a.status}</span></td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          disabled={actingId === a.id}
                          onClick={() => toggleStatus(a.id, a.status)}
                        >
                          {a.status === 'active' ? 'Suspendre' : 'Activer'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Clients ({clients.data?.total ?? '…'})</h2>
          {clients.isLoading ? (
            <div className="state-box"><span className="loading-spinner" /></div>
          ) : clients.error ? (
            <div className="state-box">{clients.error}</div>
          ) : !clients.data?.items.length ? (
            <div className="state-box">Aucun client pour le moment.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Statut</th><th>Inscrit le</th></tr>
                </thead>
                <tbody>
                  {clients.data.items.map(c => (
                    <tr key={c.id}>
                      <td><strong>{c.fullName}</strong></td>
                      <td>{c.email}</td>
                      <td>{c.phone}</td>
                      <td><span className={`badge ${userStatusColors[c.status] ?? 'badge-gray'}`}>{userStatusLabels[c.status] ?? c.status}</span></td>
                      <td>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
